import React, { useMemo, useState } from 'react'
import type { Guide } from '@/lib/guides/types/guide'
import { IDENTITY_PROJECTION, spreadCenterProjection } from '@/lib/canvas/document-projection'
import type {
  BookSettingPayload,
  RulerUnit,
  UnitOfMeasureValue,
} from '@/api/services/book-setting.service'
import { type SnapSettingsLoaded, clampSnapDistancePt } from '@/api/services/snap-settings.service'
import {
  DEFAULT_VIEW_SETTINGS,
  type ViewSettingsDto,
  showGridStringToGridMode,
} from '@/api/services/view-settings.service'
import { PRINT_BLEED_MARGIN_PT } from '@/lib/document/print-bleed'
import type { PreferencesValues } from '@/components/app/layout/preferences-dialog'
import type {
  GuideSettingsState,
  SpreadGuideSettingsLoaded,
} from '@/lib/guides/guide-settings-precedence'
import { pageViewModeFromSpreadPages } from '@/api/hooks/use-spreads'
import type { SpreadBleedSize } from '@/lib/clikart/sync-clikart-placement-from-shapes'
import type { DocumentLayoutState } from '../../types/document-layout-state'
import type { PageViewMode } from '../../types/page-view-mode'
import { DEFAULT_DOCUMENT_LAYOUT } from '../../constants/default-document-layout'
import { loadGuideDefaults } from '../../helpers/guide-defaults-storage'
import type { HomeZoomAndSpread } from '../../types/home-groups'

/**
 * Document and view state: view / snap / guide visibility, guide grid and book settings state, the
 * guides and resize dialogs, preferences, and the derived page layout (trim/bleed sizes, spread
 * projection, page view mode, page origin, mirror axis).
 */
export function useHomeDocumentLayout(
  deps: Pick<HomeZoomAndSpread, 'fitToSpreadViewportOk' | 'activeSpread'>
) {
  const { fitToSpreadViewportOk, activeSpread } = deps

  /** Snap to grid: source of truth is view settings GET/PUT (not book settings) */
  const [viewSettings, setViewSettings] = React.useState<ViewSettingsDto>(DEFAULT_VIEW_SETTINGS)
  const [spreadViewSettings, setSpreadViewSettings] = React.useState<ViewSettingsDto | null>(null)
  const { snapToGrid } = viewSettings
  // Grid overlay: driven by view settings `showGrid` (GET/PUT), not book settings
  const gridMode = useMemo(
    () => showGridStringToGridMode(viewSettings.showGrid),
    [viewSettings.showGrid]
  )
  const initialGuideDefaults = React.useMemo(() => loadGuideDefaults(), [])
  const [showGuides, setShowGuides] = React.useState(initialGuideDefaults.showGuides)
  const [showSmartGuides, setShowSmartGuides] = React.useState(initialGuideDefaults.showSmartGuides)
  /** Source of truth: GET snap settings; not stored in localStorage */
  const [snapToGuides, setSnapToGuides] = React.useState(false)
  const [spreadSnapSettings, setSpreadSnapSettings] = React.useState<SnapSettingsLoaded | null>(
    null
  )

  const [guidesPerSpread, setGuidesPerSpread] = React.useState<Record<string, Guide[]>>({})
  /** null until book settings payload is applied — avoids a wrong picas flash on the rulers */
  const [rulerUnit, setRulerUnit] = React.useState<RulerUnit | null>(null)
  const [unitOfMeasure, setUnitOfMeasure] = React.useState<UnitOfMeasureValue>('Picas')
  const [columns, setColumns] = React.useState(0)
  const [rows, setRows] = React.useState(0)
  const [colGutter, setColGutter] = React.useState(24)
  const [rowGutter, setRowGutter] = React.useState(24)
  const [defaultGuideGuttersLinked, setDefaultGuideGuttersLinked] = React.useState(true)
  const [spreadGuideSettings, setSpreadGuideSettings] =
    React.useState<SpreadGuideSettingsLoaded | null>(null)
  const [spreadGuideUpdatedAt, setSpreadGuideUpdatedAt] = React.useState<{
    column?: string
    row?: string
  }>({})
  const [defaultGuideSettings, setDefaultGuideSettings] = React.useState<GuideSettingsState | null>(
    null
  )
  const [bookSettingsPayload, setBookSettingsPayload] = React.useState<BookSettingPayload | null>(
    null
  )
  const [hasLoadedBookSettings, setHasLoadedBookSettings] = React.useState(false)
  const [hasLoadedSpreadGuideSettings, setHasLoadedSpreadGuideSettings] = React.useState(false)
  const [loadedSpreadGuideSettingsFor, setLoadedSpreadGuideSettingsFor] = React.useState<string>('')
  const [isGuidesDialogOpen, setIsGuidesDialogOpen] = React.useState(false)
  const [isResizeLayoutDialogOpen, setIsResizeLayoutDialogOpen] = React.useState(false)
  const [documentLayout, setDocumentLayout] =
    React.useState<DocumentLayoutState>(DEFAULT_DOCUMENT_LAYOUT)

  const [totalPages, setTotalPages] = React.useState(0)

  const [preferencesValues, setPreferencesValues] = React.useState<PreferencesValues>({
    nudge: '1',
    snapDistance: '12',
  })
  const hasCheckedLayoutFitForSpreadRef = React.useRef<Record<string, boolean>>({})

  const nudgeDistancePt = useMemo(() => {
    const t = preferencesValues.nudge.trim()

    if (!/^\d+$/.test(t)) return 1
    const n = parseInt(t, 10)

    return n >= 1 && n <= 100 ? n : 1
  }, [preferencesValues.nudge])

  /** Preferences: max distance (pt) for snapping to ruler guides / smart guides — same 1–24 pt as API and Preferences dialog. */
  const snapGuideDistancePt = useMemo(
    () => clampSnapDistancePt(parseInt(preferencesValues.snapDistance.trim(), 10)),
    [preferencesValues.snapDistance]
  )

  const pageViewMode: PageViewMode = useMemo(
    () => pageViewModeFromSpreadPages(activeSpread?.pages),
    [activeSpread]
  )

  const resolvedLayout = useMemo(() => {
    const pageTrimWidth = Math.max(1, documentLayout.pageTrimWidth)
    const pageTrimHeight = Math.max(1, documentLayout.pageTrimHeight)
    const trimWidth = pageTrimWidth * 2
    const trimHeight = pageTrimHeight
    const bleedWidth = trimWidth + 2 * PRINT_BLEED_MARGIN_PT
    const bleedHeight = trimHeight + 2 * PRINT_BLEED_MARGIN_PT
    const pageInnerWidth = Math.max(
      1,
      pageTrimWidth - documentLayout.marginInside - documentLayout.marginOutside
    )
    const pageInnerHeight = Math.max(
      1,
      pageTrimHeight - documentLayout.marginTop - documentLayout.marginBottom
    )

    return {
      trimWidth,
      trimHeight,
      bleedWidth,
      bleedHeight,
      pageInnerWidth,
      pageInnerHeight,
    }
  }, [documentLayout])

  // The spread box, plus which half is the page on a single-page spread (the other half is drawn
  // as pasteboard). The ClikArt "Placed" check needs both: see `collectPlacedClikArtIds`.
  const spreadPlacementBounds = useMemo<SpreadBleedSize>(
    () => ({
      bleedWidth: resolvedLayout.bleedWidth,
      bleedHeight: resolvedLayout.bleedHeight,
      singlePageSide:
        pageViewMode === 'single-first' ? 'first' : pageViewMode === 'single-last' ? 'last' : null,
    }),
    [resolvedLayout.bleedWidth, resolvedLayout.bleedHeight, pageViewMode]
  )

  const spreadProjection = React.useMemo(() => {
    if (!hasLoadedBookSettings) return IDENTITY_PROJECTION

    return spreadCenterProjection({
      trimWidth: resolvedLayout.trimWidth,
      trimHeight: resolvedLayout.trimHeight,
      bleedMargin: PRINT_BLEED_MARGIN_PT,
      bleedWidth: resolvedLayout.bleedWidth,
      bleedHeight: resolvedLayout.bleedHeight,
    })
  }, [hasLoadedBookSettings, resolvedLayout])

  // Legacy renders a horizontal guide as a line pinned at the left edge of the spread's
  // leftmost page, fixed-width across every page in the spread — so its wire `X` must be that
  // page's left edge in the spine-relative frame, not a placeholder. `single-first` (front
  // cover) has no left page, so its "leftmost page" left edge IS the spine (centered X = 0);
  // every other case (double spread, `single-last` back cover) has a real left page starting
  // at the bleed box's trim edge (doc-px `PRINT_BLEED_MARGIN_PT`).
  const horizontalGuideAnchorX =
    pageViewMode === 'single-first' ? 0 : PRINT_BLEED_MARGIN_PT - spreadProjection.offsetX

  const hasDocumentForFit = React.useMemo(
    () => totalPages > 0 && resolvedLayout.bleedWidth > 0 && resolvedLayout.bleedHeight > 0,
    [totalPages, resolvedLayout.bleedWidth, resolvedLayout.bleedHeight]
  )

  const fitToSpreadDisabled = !hasDocumentForFit || !fitToSpreadViewportOk

  // Origin for object panel position: matches ruler zero.
  // single-last (LEFT page): origin at outer edge. single-first (RIGHT page) and spread: origin at gutter.
  const rightPageOrigin = useMemo(() => {
    const pageTrimWidth = Math.max(0, Math.floor(resolvedLayout.trimWidth / 2))

    if (pageViewMode === 'single-last') {
      return {
        x: PRINT_BLEED_MARGIN_PT,
        y: PRINT_BLEED_MARGIN_PT,
      }
    }

    return {
      x: PRINT_BLEED_MARGIN_PT + pageTrimWidth,
      y: PRINT_BLEED_MARGIN_PT,
    }
  }, [resolvedLayout.trimWidth, pageViewMode])

  // Mirror Move reflects across the spread's spine; a single-last page has no facing
  // page/spine to mirror across.
  const mirrorSpineAxisX = pageViewMode === 'single-last' ? null : rightPageOrigin.x
  const [isMirrorMoveHovering, setIsMirrorMoveHovering] = useState(false)

  return {
    snapToGrid,
    viewSettings,
    setViewSettings,
    spreadViewSettings,
    setSpreadViewSettings,
    showGuides,
    setShowGuides,
    showSmartGuides,
    setShowSmartGuides,
    snapToGuides,
    setSnapToGuides,
    spreadSnapSettings,
    setSpreadSnapSettings,
    guidesPerSpread,
    setGuidesPerSpread,
    rulerUnit,
    setRulerUnit,
    unitOfMeasure,
    setUnitOfMeasure,
    columns,
    setColumns,
    rows,
    setRows,
    colGutter,
    setColGutter,
    rowGutter,
    setRowGutter,
    defaultGuideGuttersLinked,
    setDefaultGuideGuttersLinked,
    spreadGuideSettings,
    setSpreadGuideSettings,
    spreadGuideUpdatedAt,
    setSpreadGuideUpdatedAt,
    defaultGuideSettings,
    setDefaultGuideSettings,
    bookSettingsPayload,
    setBookSettingsPayload,
    hasLoadedBookSettings,
    setHasLoadedBookSettings,
    hasLoadedSpreadGuideSettings,
    setHasLoadedSpreadGuideSettings,
    loadedSpreadGuideSettingsFor,
    setLoadedSpreadGuideSettingsFor,
    isGuidesDialogOpen,
    setIsGuidesDialogOpen,
    isResizeLayoutDialogOpen,
    setIsResizeLayoutDialogOpen,
    documentLayout,
    setDocumentLayout,
    totalPages,
    setTotalPages,
    preferencesValues,
    setPreferencesValues,
    isMirrorMoveHovering,
    setIsMirrorMoveHovering,
    gridMode,
    initialGuideDefaults,
    hasCheckedLayoutFitForSpreadRef,
    nudgeDistancePt,
    snapGuideDistancePt,
    pageViewMode,
    resolvedLayout,
    spreadPlacementBounds,
    spreadProjection,
    horizontalGuideAnchorX,
    hasDocumentForFit,
    fitToSpreadDisabled,
    rightPageOrigin,
    mirrorSpineAxisX,
  }
}
